// Two-step inline confirm for an action that breaks something live.
//
// The pattern is lifted from `screens/ops/LicenseKeysCard.tsx`, which already
// ships it: arm, name the consequence in a `role="alert"`, then confirm. There
// is no modal or dialog primitive in `src/ui/` and none is needed for this.
//
// Not in `src/ui/`: two callers, both inside admin. `screens/content/controls.tsx`
// and `screens/ops/CopyField.tsx` are the precedent for a screen-local control.
// `LicenseKeysCard` is deliberately NOT refactored onto this — it is working
// code behind a different fence, and its last-active-key sentence is bespoke.
// If a third caller appears, that is the trigger to hoist this into `ui/`.

import { useState } from "react";
import { Button } from "../../ui";

export type ConfirmButtonProps = {
  label: string;
  confirmLabel: string;
  /** What breaks. Stated before the destructive click, not after. */
  consequence: string;
  pending?: boolean;
  onConfirm: () => void;
};

export function ConfirmButton({
  label,
  confirmLabel,
  consequence,
  pending = false,
  onConfirm,
}: ConfirmButtonProps) {
  const [armed, setArmed] = useState(false);

  if (!armed) {
    return (
      <Button variant="secondary" size="sm" onClick={() => setArmed(true)}>
        {label}
      </Button>
    );
  }

  return (
    <span className="maisight-admin-confirm">
      <p className="maisight-admin-confirm-consequence type-bodyS" role="alert">
        {consequence}
      </p>
      <Button
        variant="danger"
        size="sm"
        onClick={() => {
          setArmed(false);
          onConfirm();
        }}
      >
        {pending ? "Working…" : confirmLabel}
      </Button>
      <Button variant="ghost" size="sm" onClick={() => setArmed(false)}>
        Cancel
      </Button>
    </span>
  );
}
